import { BehaviorSubject } from 'rxjs';

import { MapSelection } from './map-selection';

import { MapObjectId } from './map-object';

/** Selected business IDs belonging to one feature. */
export class ObjectSelection<TId extends MapObjectId = MapObjectId> implements MapSelection<TId> {
  private readonly selectedIds = new Set<TId>();
  private readonly changedSubject = new BehaviorSubject<readonly TId[]>([]);
  readonly changed$ = this.changedSubject.asObservable();

  capture(): readonly TId[] { return this.ids; }
  restore(ids: readonly TId[]): void { this.replace(ids); }

  get ids(): TId[] { return [...this.selectedIds]; }
  get count(): number { return this.selectedIds.size; }
  get size(): number { return this.selectedIds.size; }

  has(id: TId): boolean {
    return this.selectedIds.has(id);
  }

  toggle(id: TId): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.changedSubject.next([id]);
  }

  add(ids: readonly TId[]): void {
    const changed: TId[] = [];
    for (const id of ids) {
      if (this.selectedIds.has(id)) continue;
      this.selectedIds.add(id);
      changed.push(id);
    }
    if (changed.length) this.changedSubject.next(changed);
  }

  append(ids: readonly TId[]): void {
    this.add(ids);
  }

  remove(ids: readonly TId[]): void {
    const changed: TId[] = [];
    for (const id of ids) {
      if (!this.selectedIds.delete(id)) continue;
      changed.push(id);
    }
    if (changed.length) this.changedSubject.next(changed);
  }

  replace(ids: readonly TId[]): void {
    const next = new Set(ids);
    const candidates = new Set([...this.selectedIds, ...next]);
    const changed = [...candidates]
      .filter(id => this.selectedIds.has(id) !== next.has(id));
    if (!changed.length) return;

    this.selectedIds.clear();
    for (const id of next) this.selectedIds.add(id);
    this.changedSubject.next(changed);
  }

  clear(): void {
    if (!this.selectedIds.size) return;
    const changed = this.ids;
    this.selectedIds.clear();
    this.changedSubject.next(changed);
  }
}
